import { useEffect, useRef, useState } from 'react';

import { useSaveUiState, useUiState } from '../../../services/uiState';

// Two looks (Nic 2026-10-24): the classic dashboard by default, and the new
// one for anyone who turns on "Try the new look". Same data, rules and
// filters; only the screen differs. The choice is saved to the account
// (ui-state dashboardLook) so it follows them anywhere, and to this browser
// per person so the right look paints first, with no flash of the other.
export const LOOKS = ['classic', 'new'];
export const SAVE_AFTER_MS = 600;
const lookKey = (userId) => `dashboard_look:${userId || 'anon'}`;
const readLook = (userId) => {
  try {
    const v = window.localStorage.getItem(lookKey(userId));
    return LOOKS.includes(v) ? v : 'classic';
  } catch {
    return 'classic';
  }
};
const writeLook = (userId, v) => {
  try {
    window.localStorage.setItem(lookKey(userId), v);
  } catch {
    /* storage blocked: the server copy still holds it */
  }
};

// Returns [look, setLook]. Once the server answers, its choice wins
// (another computer may have changed it).
const useDashboardLook = (userId) => {
  const [look, setLookState] = useState(() => readLook(userId));
  const ui = useUiState();
  const saveUiState = useSaveUiState();
  const serverReady = useRef(false);
  useEffect(() => {
    if (serverReady.current || ui.isLoading || ui.isError) return;
    serverReady.current = true;
    const saved = ui.data?.dashboardLook;
    if (LOOKS.includes(saved) && saved !== look) {
      setLookState(saved);
      writeLook(userId, saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.isLoading, ui.isError]);
  // Flipping back and forth quickly sends one save, the last choice, once
  // the clicking stops: separate saves could land out of order and leave
  // the account on the other look. The timer isn't cleared on unmount, so a
  // flip right before leaving the page still saves.
  const saveTimer = useRef(null);
  const setLook = (next) => {
    if (!LOOKS.includes(next)) return;
    setLookState(next);
    writeLook(userId, next);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveUiState('dashboardLook', next), SAVE_AFTER_MS);
  };
  return [look, setLook];
};

export default useDashboardLook;
